import { MAX_REQUEST_ITEM_QUANTITY, type CreateServiceRequestItemInput } from '@hotel-voice/shared';
import { multiplyMoney, sumMoney } from '@/lib/money';
import type { MenuItem } from '@/types';

/**
 * The restaurant cart: quantities by menu item id, plus the note for the
 * chef. A plain reducer so the arithmetic is testable without React, and so
 * the order sheet, the menu badges and the submit payload all derive from one
 * value rather than three copies of it.
 *
 * Prices here are for *display*. The server re-prices the order from its own
 * copy of the menu when it is placed; a total shown from stale data is a
 * cosmetic slip, never a wrong charge.
 */

export interface Cart {
  /** itemId → quantity. Never holds a zero; removing sets nothing. */
  quantities: Record<string, number>;
  note: string;
}

export const EMPTY_CART: Cart = { quantities: {}, note: '' };

export type CartAction =
  | { type: 'add'; itemId: string }
  | { type: 'set'; itemId: string; quantity: number }
  | { type: 'remove'; itemId: string }
  | { type: 'note'; note: string }
  | { type: 'clear' };

export function cartReducer(cart: Cart, action: CartAction): Cart {
  switch (action.type) {
    case 'add': {
      const current = cart.quantities[action.itemId] ?? 0;
      if (current >= MAX_REQUEST_ITEM_QUANTITY) return cart;
      return { ...cart, quantities: { ...cart.quantities, [action.itemId]: current + 1 } };
    }
    case 'set': {
      const quantity = Math.min(MAX_REQUEST_ITEM_QUANTITY, Math.floor(action.quantity));
      if (quantity <= 0) return cartReducer(cart, { type: 'remove', itemId: action.itemId });
      return { ...cart, quantities: { ...cart.quantities, [action.itemId]: quantity } };
    }
    case 'remove': {
      if (!(action.itemId in cart.quantities)) return cart;
      const { [action.itemId]: _removed, ...rest } = cart.quantities;
      return { ...cart, quantities: rest };
    }
    case 'note':
      return { ...cart, note: action.note };
    case 'clear':
      return EMPTY_CART;
  }
}

export interface CartLine {
  item: MenuItem;
  quantity: number;
  lineTotal: number;
}

/**
 * The cart joined to the menu, in menu order. An id with no matching dish
 * (the dish was switched off after it was added) is dropped from the lines
 * and from the count, so the guest cannot try to order it.
 */
export function cartLines(cart: Cart, menu: readonly MenuItem[]): CartLine[] {
  return menu.flatMap((item) => {
    const quantity = cart.quantities[item.id];
    if (!quantity) return [];
    return [{ item, quantity, lineTotal: multiplyMoney(item.price, quantity) }];
  });
}

export function cartTotal(lines: readonly CartLine[]): number {
  return sumMoney(lines.map((line) => line.lineTotal));
}

export function cartCount(lines: readonly CartLine[]): number {
  return lines.reduce((sum, line) => sum + line.quantity, 0);
}

export function toRequestItems(lines: readonly CartLine[]): CreateServiceRequestItemInput[] {
  return lines.map((line) => ({ id: line.item.id, quantity: line.quantity }));
}

/** Dishes grouped under the menu's categories, in the menu's order; unlisted categories go last. */
export function groupByCategory(
  categories: readonly string[],
  items: readonly MenuItem[],
): Array<{ category: string; items: MenuItem[] }> {
  const buckets = new Map<string, MenuItem[]>(categories.map((c) => [c, []]));
  const other: MenuItem[] = [];
  for (const item of items) {
    const bucket = buckets.get(item.category);
    if (bucket) bucket.push(item);
    else other.push(item);
  }
  const groups = [...buckets.entries()]
    .map(([category, list]) => ({ category, items: list }))
    .filter((group) => group.items.length > 0);
  if (other.length) groups.push({ category: 'Other', items: other });
  return groups;
}
